import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Phone, Mail } from "lucide-react";
import { serviceAreas } from "@/data/serviceAreas";
import { serviceAreaSeo } from "@/data/serviceAreaSeo";
import { ContextualLinks } from "@/components/ContextualLinks";
import { guidesForTopics, nearbyCityLinks, servicesForTopics } from "@/lib/internalLinks";
import { neighborhoodPages } from "@/data/neighborhoodPages";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const heroImg = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { BUSINESS_REF, breadcrumbSchema, serviceSchema } from "@/lib/localBusinessSchema";
import { faqPageSchema } from "@/data/serviceFaqs";
import { areaFaqs } from "@/data/areaFaqs";
import { areaLongformFor } from "@/data/areaLongform";

export const Route = createFileRoute("/service-areas/$slug")({
  loader: ({ params }) => {
    const area = serviceAreas.find((a) => a.slug === params.slug);
    if (!area) throw notFound();
    return { area };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.area;
    if (!a) return { meta: [{ title: "Service Area Not Found | Apex AV" }] };
    const seo = serviceAreaSeo[a.slug];
    const title = seo?.title ?? `${a.name}, CO Smart Home & AV Installation | Apex AV`;
    const desc =
      seo?.description ??
      `Home theater, smart home, whole-home audio, lighting & motorized shade installation in ${a.name}, CO (${a.zip}). Free in-home consultations.`;
    const keywords = seo
      ? [seo.primaryKeyword, ...seo.secondaryKeywords, `${a.zip} smart home`].join(", ")
      : `home automation ${a.name}, AV installation ${a.name}, smart home ${a.name} CO, home theater installer ${a.name}, ${a.name} lighting control, ${a.zip} smart home`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        {
          name: "keywords",
          content: keywords,
        },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `https://apexav.com/service-areas/${a.slug}` },
        { property: "og:type", content: "website" },
        { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
        { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      ],
      links: [
        { rel: "canonical", href: `https://apexav.com/service-areas/${a.slug}` },
        { rel: "preload", as: "image", href: heroImg, fetchPriority: "high" } as never,
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
                "@id": `https://apexav.com/service-areas/${a.slug}#localbusiness`,
                name: `Apex AV — ${a.name}, CO`,
                description: desc,
                image: `https://apexav.com${apexTheaterRichImg.url}`,
                logo: "https://apexav.com/logo.png",
                url: `https://apexav.com/service-areas/${a.slug}`,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "2921 W 38th AVE STE 169",
                  addressLocality: "Denver",
                  addressRegion: "CO",
                  postalCode: "80211",
                  addressCountry: "US",
                },
                geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
                telephone: "+1-303-317-3233",
                email: "info@apexav.com",
                priceRange: "$$$",
                areaServed: [
                  { "@type": "City", name: `${a.name}, CO` },
                  { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
                ],
                openingHoursSpecification: [
                  { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "18:00" },
                  { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
                ],
                sameAs: [
                  "https://share.google/jMyCOceBfsaHfeVQp",
                  "https://x.com/ApexAV80211",
                  "https://www.facebook.com/apexavdenver",
                ],
                parentOrganization: BUSINESS_REF,
              },
              serviceSchema({
                name: `Home Automation & AV Installation in ${a.name}, CO`,
                description: `Custom home theater, smart home automation, whole-home audio, lighting, and shading installation in ${a.name}, Colorado.`,
                url: `https://apexav.com/service-areas/${a.slug}`,
                serviceType: "Home Automation & AV Installation",
                areaServed: `${a.name}, CO`,
              }),
              faqPageSchema(areaFaqs(a), `https://apexav.com/service-areas/${a.slug}`),
              breadcrumbSchema([
                { name: "Home", url: "https://apexav.com/" },
                { name: "Service Areas", url: "https://apexav.com/service-areas" },
                { name: a.name, url: `https://apexav.com/service-areas/${a.slug}` },
              ]),
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-3xl font-bold">Service area not found</h1>
      <Link to="/service-areas" className="mt-6 inline-block text-brand-text">
        View all areas →
      </Link>
    </div>
  ),
  component: AreaPage,
});

const services = [
  { label: "Custom home theater design & install", to: "/services/home-theater" as const, link: true },
  { label: "Smart home automation & integrated control", to: "/services/smart-home" as const, link: true },
  { label: "Whole-home audio (in-wall / in-ceiling)", to: "/services/whole-home-audio" as const, link: true },
  { label: "Hidden & invisible speakers", to: "/services/hidden-audio" as const, link: true },
  { label: "Whole-home 4K HDR video distribution", to: "/services/whole-home-video" as const, link: true },
  { label: "Smart climate control & multi-zone HVAC", to: "/services/climate-control" as const, link: true },
  { label: "Architectural lighting design & keypads", to: "/services/lighting" as const, link: true },
  { label: "Motorized shades & window treatments", to: "/services/shading" as const, link: true },
  { label: "Outdoor TVs & landscape audio", to: "/services/outdoor" as const, link: true },
  { label: "4K security cameras & smart locks", to: "/services/security" as const, link: true },
  { label: "Enterprise-grade Wi-Fi networking", to: "/services/networking" as const, link: true },
  { label: "TV mounting & in-wall power", to: "/services/tv-mounting" as const, link: true },
  { label: "New construction prewire", to: "/services/prewire" as const, link: true },
  { label: "Retrofit & cable fishing", to: "/services/retrofit" as const, link: true },
];

function AreaPage() {
  const { area } = Route.useLoaderData();
  const localPages = neighborhoodPages.filter((p) => p.neighborhoodSlug === area.slug);
  const faqs = areaFaqs(area);
  const longform = areaLongformFor(area.slug);
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt={`Custom home theater and smart home installation in ${area.name}, Colorado by Apex AV Denver`}
            className="h-full w-full object-cover"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>
        <div className="relative container mx-auto px-6 py-24 md:py-32">
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <MapPin className="h-4 w-4" /> All Service Areas
          </Link>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold max-w-3xl">
            Home Automation & AV Installation in{" "}
            <span className="text-gradient-brand">
              {area.name}, CO
            </span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{area.blurb}</p>
          <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-2xl">
            Local experts for <strong>home automation in {area.name}</strong>, <strong>AV installation in {area.name}</strong>, <strong>home theater installation</strong>, <strong>whole-home audio</strong>, <strong>smart lighting control</strong>, and <strong>motorized shades</strong>.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow"
            >
              Get a Free {area.name} Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+13033173233"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card/50 backdrop-blur px-6 py-3 text-sm font-semibold"
            >
              <Phone className="h-4 w-4" /> Call (303) 317-3233
            </a>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold">
              Trusted AV installers serving {area.name}
            </h2>
            {area.localIntro ? (
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">{area.localIntro}</p>
            ) : (
              <>
                <p className="mt-5 text-muted-foreground text-lg">
                  Apex AV has been the go-to <strong>home automation</strong> and <strong>audio video installation company in {area.name}</strong> for homeowners for over a decade. Whether you're outfitting a new build, upgrading a remodel, or adding a single zone of audio, our local team designs systems that look invisible and perform flawlessly.
                </p>
                <p className="mt-4 text-muted-foreground">
                  We specify and install only top-tier <strong>audio systems</strong>, <strong>video distribution</strong>, <strong>smart lighting</strong>, and <strong>whole-home automation</strong> platforms — and we provide ongoing support so your system keeps working long after install day. Free <strong>in-home AV consultations in {area.name}</strong> ({area.zip}) and surrounding neighborhoods.
                </p>
              </>
            )}

            {area.commonHomes && area.commonHomes.length > 0 && (
              <div className="mt-10">
                <h3 className="font-display text-2xl font-bold">Homes we typically work on in {area.name}</h3>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {area.commonHomes.map((h: string) => (
                    <li key={h} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-brand-text shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {area.hoaNotes && (
              <div className="mt-10 rounded-xl border border-border bg-card p-6 shadow-card">
                <h3 className="font-display text-lg font-bold">HOA, permits & local review in {area.name}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{area.hoaNotes}</p>
              </div>
            )}

            <h3 className="mt-12 font-display text-2xl font-bold">Services available in {area.name}</h3>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {services.map((s) =>
                s.link ? (
                  <li key={s.label}>
                    <Link
                      to={s.to}
                      className="group flex items-start gap-3 text-sm hover:text-brand-text transition"
                    >
                      <CheckCircle2 className="h-5 w-5 text-brand-text shrink-0 mt-0.5" />
                      <span className="underline-offset-4 group-hover:underline">{s.label}</span>
                    </Link>
                  </li>
                ) : (
                  <li key={s.label} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-brand-text shrink-0 mt-0.5" />
                    <span>{s.label}</span>
                  </li>
                )
              )}
            </ul>

            {localPages.length > 0 && (
              <div className="mt-12">
                <h3 className="font-display text-2xl font-bold">
                  Featured {area.name} services
                </h3>
                <p className="mt-3 text-muted-foreground">
                  Deep-dive guides written specifically for {area.name} homes.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {localPages.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="group rounded-xl border border-border bg-card p-5 shadow-card hover:border-brand/50 transition"
                    >
                      <h4 className="font-display text-lg font-semibold group-hover:text-brand-text">
                        {p.label}
                      </h4>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm text-brand-text">
                        Read the {area.name} guide <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="overflow-hidden rounded-xl border border-border shadow-card">
              <iframe
                title={`Map of ${area.name}, CO ${area.zip}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(area.name + ", CO " + area.zip)}&output=embed`}
                width="100%"
                height="240"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block w-full border-0"
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6 shadow-card">
              <h3 className="font-display text-lg font-bold">Neighborhoods we serve</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {area.highlights.map((h: string) => (
                  <li key={h} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand" /> {h}
                  </li>
                ))}
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Zip code {area.zip}
                </li>
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-gradient-to-br from-card to-background p-6 shadow-card">
              <h3 className="font-display text-lg font-bold">Free {area.name} consultation</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                No-pressure walkthrough and quote in your home.
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-brand-foreground"
              >
                Request Quote
              </Link>
              <div className="mt-5 space-y-2 text-sm text-muted-foreground">
                <a
                  href="tel:+13033173233"
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  <Phone className="h-4 w-4 text-brand-text" /> (303) 317-3233
                </a>
                <a
                  href="mailto:info@apexav.com"
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  <Mail className="h-4 w-4 text-brand-text" /> info@apexav.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {longform.length > 0 && (
        <section className="border-t border-border">
          <div className="container mx-auto px-6 py-20">
            <h2 className="font-display text-3xl md:text-4xl font-bold">
              What to know about AV installation in {area.name}
            </h2>
            <div className="mt-8 grid gap-10 lg:grid-cols-2">
              {longform.map((section) => (
                <div key={section.heading}>
                  <h3 className="font-display text-xl font-semibold">{section.heading}</h3>
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="mt-4 text-muted-foreground leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-border bg-card/30">
        <div className="container mx-auto px-6 py-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold">
            {area.name} smart home & AV installation FAQs
          </h2>
          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-xl border border-border bg-card p-6 shadow-card">
                <h3 className="font-display text-lg font-semibold">{f.q}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      <ContextualLinks
        heading={`Guides and services for ${area.name} homeowners`}
        intro={`Planning research and the neighboring Front Range communities we cover alongside ${area.name}.`}
        groups={[
          {
            heading: `Guides for ${area.name} projects`,
            icon: "guide",
            links: guidesForTopics(["home-theater", "smart-home", "lighting", "networking"], 4),
          },
          {
            heading: `Popular services in ${area.name}`,
            icon: "service",
            links: servicesForTopics(["tv-mounting", "smart-home", "home-theater", "networking"], 4),
          },
          {
            heading: `Near ${area.name}`,
            icon: "area",
            links: nearbyCityLinks(area.slug, 5),
          },
        ]}
      />


      <section className="container mx-auto px-6 pb-20">
        <h2 className="font-display text-2xl font-bold mb-6">Other Denver Metro service areas</h2>
        <div className="flex flex-wrap gap-3">
          {serviceAreas
            .filter((a) => a.slug !== area.slug)
            .map((a) => (
              <Link
                key={a.slug}
                to="/service-areas/$slug"
                params={{ slug: a.slug }}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-brand/50 transition"
              >
                {a.name}
              </Link>
            ))}
        </div>
      </section>
    </>
  );
}
